import type { ErrorEvent, EventHint } from "@sentry/react";
import { AxiosError, isAxiosError } from "axios";

/*
  Most of what reaches Sentry comes from the ad stack (Mediavine, its bidders,
  GPT) and from browser extensions. Their hosts change every week, so these
  rules allowlist what is provably ours instead of denylisting theirs.
*/

export type NoiseVerdict = "keep" | "drop" | "sample";

// Share of the "sample" class that is still sent, so a new first-party
// failure in it stays visible.
export const NOISE_SAMPLE_RATE = 0.05;

// Sentry's fetch instrumentation appends the request host to these messages.
const FETCH_FAILURE =
  /^(?:Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.) \((.+)\)$/;
const OUR_HOST = /(^|\.)easysbc\.io(:\d+)?$/;
// Axios's messages for a request that got no response.
const NO_RESPONSE_MESSAGE =
  /^(?:Network Error|Request aborted|canceled|timeout of \d+ms exceeded)$/;

const mainException = (event: ErrorEvent) => event.exception?.values?.at(-1);

const isUnhandled = (event: ErrorEvent) =>
  mainException(event)?.mechanism?.handled === false;

const hasFrames = (event: ErrorEvent) =>
  !!event.exception?.values?.some((exception) =>
    exception.stacktrace?.frames?.some((frame) => !!frame.filename),
  );

export const classifySentryEvent = (
  event: ErrorEvent,
  hint?: EventHint,
): NoiseVerdict => {
  const original = hint?.originalException;
  if (isAxiosError(original)) {
    // Reported once per endpoint by the response interceptor.
    if (!original.response) return "drop";
    // The interceptor has already logged the user out and redirected.
    if (original.response.status === 401 && isUnhandled(event)) return "drop";
    return "keep";
  }

  // Catch blocks that forward error.message to captureMessage.
  if (!event.exception && NO_RESPONSE_MESSAGE.test(event.message ?? "")) {
    return "drop";
  }

  const exception = mainException(event);
  if (!exception) return "keep";

  // The SDK's fetch wrapper is the top frame of every failed fetch, so the
  // host is the only reliable tell of whose request it was.
  const fetchHost =
    exception.type === "TypeError"
      ? exception.value?.match(FETCH_FAILURE)?.[1]
      : undefined;
  if (fetchHost) return OUR_HOST.test(fetchHost) ? "keep" : "drop";

  // Nothing to attribute it by: ad XHR rejections, extension messages.
  if (
    isUnhandled(event) &&
    !hasFrames(event) &&
    !exception.value?.includes("easysbc.io")
  ) {
    return "sample";
  }

  return "keep";
};

export const filterSentryNoise = (
  event: ErrorEvent,
  hint?: EventHint,
  random: () => number = Math.random,
): ErrorEvent | null => {
  const verdict = classifySentryEvent(event, hint);
  if (verdict === "drop") return null;
  if (verdict === "sample") {
    if (random() >= NOISE_SAMPLE_RATE) return null;
    event.tags = { ...event.tags, noise_sampled: true };
  }
  return event;
};

// "/players/226161?x=1" -> "/players/:id", so failures group per endpoint.
export const endpointTemplate = (url: string | undefined): string => {
  if (!url) return "unknown";
  let path: string;
  try {
    path = new URL(url, "http://relative").pathname;
  } catch {
    path = url.split("?")[0];
  }
  return path.replace(
    /\/(?:\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\/|$)/gi,
    "/:id",
  );
};

/** Key to report a no-response failure under, or null when it isn't ours. */
export const noResponseReportKey = (
  error: AxiosError,
  { online, hidden }: { online: boolean; hidden: boolean },
): string | null => {
  if (error.response || error.code === AxiosError.ERR_CANCELED) return null;
  // Offline or backgrounded: the browser dropped it, not the API.
  if (!online || hidden) return null;
  const method = (error.config?.method ?? "get").toUpperCase();
  return `${method} ${endpointTemplate(error.config?.url)}`;
};
